export const MINIMUM_ACCOUNT_AGE = 13;

const dateOfBirthPattern = /^\d{4}-\d{2}-\d{2}$/;
const isoLikeDatePattern = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;
const usNumericDatePattern = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/;
const monthNameDatePattern =
  /^([a-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?[,]?\s+(\d{2}|\d{4})$/i;
const dayMonthNameDatePattern =
  /^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]+)\.?[,]?\s+(\d{2}|\d{4})$/i;

const monthNameToNumber = new Map<string, number>(
  (
    [
    ['january', 1],
    ['jan', 1],
    ['february', 2],
    ['feb', 2],
    ['march', 3],
    ['mar', 3],
    ['april', 4],
    ['apr', 4],
    ['may', 5],
    ['june', 6],
    ['jun', 6],
    ['july', 7],
    ['jul', 7],
    ['august', 8],
    ['aug', 8],
    ['september', 9],
    ['sep', 9],
    ['sept', 9],
    ['october', 10],
    ['oct', 10],
    ['november', 11],
    ['nov', 11],
    ['december', 12],
    ['dec', 12],
  ] as const
  ).map(([name, month]) => [name, month]),
);

export type NormalizedDateOfBirthResult =
  | {
      ok: true;
      value: string;
    }
  | {
      message: string;
      ok: false;
    };

function formatDateOfBirth(year: number, month: number, day: number) {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(
    2,
    '0',
  )}-${String(day).padStart(2, '0')}`;
}

function normalizeYear(value: string) {
  if (value.length === 4) {
    return Number(value);
  }

  const twoDigitYear = Number(value);
  const currentTwoDigitYear = new Date().getUTCFullYear() % 100;

  return twoDigitYear <= currentTwoDigitYear
    ? 2000 + twoDigitYear
    : 1900 + twoDigitYear;
}

function buildDateOfBirth(year: number, month: number, day: number) {
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    year < 1000 ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }

  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }

  return formatDateOfBirth(year, month, day);
}

function parseDateOfBirthParts(input: string) {
  const cleaned = input
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ', ');

  if (!cleaned) {
    return {
      message: 'Enter your date of birth.',
      ok: false,
    } as const;
  }

  let match = cleaned.match(isoLikeDatePattern);

  if (match) {
    return {
      day: Number(match[3]),
      month: Number(match[2]),
      ok: true,
      year: Number(match[1]),
    } as const;
  }

  match = cleaned.match(usNumericDatePattern);

  if (match) {
    return {
      day: Number(match[2]),
      month: Number(match[1]),
      ok: true,
      year: normalizeYear(match[3]),
    } as const;
  }

  match = cleaned.match(monthNameDatePattern);

  if (match) {
    const month = monthNameToNumber.get(match[1].toLowerCase());

    if (month) {
      return {
        day: Number(match[2]),
        month,
        ok: true,
        year: normalizeYear(match[3]),
      } as const;
    }
  }

  match = cleaned.match(dayMonthNameDatePattern);

  if (match) {
    const month = monthNameToNumber.get(match[2].toLowerCase());

    if (month) {
      return {
        day: Number(match[1]),
        month,
        ok: true,
        year: normalizeYear(match[3]),
      } as const;
    }
  }

  if (/^\d{1,2}[/-]\d{1,2}$/.test(cleaned)) {
    return {
      message: 'Include your birth year.',
      ok: false,
    } as const;
  }

  return {
    message: 'Enter a date like 5/7/1990, May 7, 1990, or 1990-05-07.',
    ok: false,
  } as const;
}

export function normalizeDateOfBirthInput(
  input: string,
): NormalizedDateOfBirthResult {
  const parsed = parseDateOfBirthParts(input);

  if (!parsed.ok) {
    return parsed;
  }

  const normalized = buildDateOfBirth(parsed.year, parsed.month, parsed.day);

  if (!normalized) {
    return {
      message: 'Enter a real calendar date for your birthday.',
      ok: false,
    };
  }

  const birthDate = new Date(`${normalized}T00:00:00.000Z`);
  const today = new Date();
  const todayUtc = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()),
  );

  if (birthDate.getTime() > todayUtc.getTime()) {
    return {
      message: 'Birthday cannot be in the future.',
      ok: false,
    };
  }

  return {
    ok: true,
    value: normalized,
  };
}

export function isValidDateOfBirth(value: string) {
  if (!dateOfBirthPattern.test(value)) {
    return false;
  }

  const date = new Date(`${value}T00:00:00.000Z`);

  if (!Number.isFinite(date.getTime())) {
    return false;
  }

  return date.toISOString().slice(0, 10) === value;
}

export function isAtLeastAge(
  dateOfBirth: string,
  minimumAge = MINIMUM_ACCOUNT_AGE,
) {
  if (!isValidDateOfBirth(dateOfBirth)) {
    return false;
  }

  const today = new Date();
  const birthDate = new Date(`${dateOfBirth}T00:00:00.000Z`);
  let age = today.getUTCFullYear() - birthDate.getUTCFullYear();
  const monthDelta = today.getUTCMonth() - birthDate.getUTCMonth();

  if (
    monthDelta < 0 ||
    (monthDelta === 0 && today.getUTCDate() < birthDate.getUTCDate())
  ) {
    age -= 1;
  }

  return age >= minimumAge;
}
